/// <amd-module name="Build/DesignerExtensions/YamlSnippetForm/Utilities/EndpointHelper" />

import { ServiceEndpointDetails } from "Build/DesignerExtensions/YamlAssistantLibrary/Contracts/Types";
import * as Resources from "Build/DesignerExtensions/YamlSnippetForm/Resources";
import { KubernetesResourceCreateParametersExistingEndpoint } from "DistributedTask/Client/Contracts/TaskAgent";
import { getTaskAgentClient } from "DistributedTask/Client/RestClient/TaskAgent";
import { AadAuthorizer } from "DistributedTask/Common/AadAuthorizer/AadAuthorizer";
import { AccessTokenRequestType, ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { getServiceEndpointClient } from "ServiceEndpoints/Client/RestClient/ServiceEndpoint";
import { getTFSPageData } from "Tfs/Platform/Page";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

interface ISubscriptionAccount {
    accountTenantId: string;
    subscriptionTenantId: string;
}

/**
 * TODO: Refactor DT types into a TypesOnly project in the future to get the benefits of using the interfaces without adding to bundle size
 * Copied interfaces from dt-subscriptionpicker
**/

export interface Subscription {
    id: string;
    displayName: string;
    subscriptionAccount: ISubscriptionAccount
}

export class EndpointConstants {
    static AuthSchemes = class {
        public static docker: string = "Docker";
        public static kubernetes: string = "Kubernetes";
        public static servicePrincipal: string = "serviceprincipal";
    };

    static EndpointTypes = class {
        public static docker: string = "dockerregistry";
        public static kubernetes: string = "Kubernetes";
        public static azureRM: string = "azurerm";
    };

    public static azureEnvironment: string = "AzureCloud";
    public static azureManagementUrl: string = "https://management.azure.com/";
}

export class EndpointHelper {

    private static _getClusterNameFromClusterId(clusterId: string): string {
        let clusterName = "";
        if (clusterId) {
            const parts = clusterId.split('/');
            const clusterNameIndex = parts.findIndex(part => part === "managedClusters") + 1;
            if (clusterNameIndex > 0) {
                clusterName = parts[clusterNameIndex];
            }
        }

        return clusterName;
    }

    public static async getAuthorizationToken(pageContext: IVssPageContext, subscription?: Subscription): Promise<string | undefined> {
        const taskClient = getTaskAgentClient(pageContext)
        const currentTenantId = await taskClient.getVstsAadTenantId();

        if (subscription && currentTenantId && currentTenantId.toLocaleLowerCase() != subscription.subscriptionAccount.subscriptionTenantId.toLocaleLowerCase()) {
            const aadAuth = new AadAuthorizer(pageContext);
            const tfspageData = getTFSPageData(pageContext);
            if (tfspageData && tfspageData.project) {
                const token = await aadAuth.getAccessTokenKey(tfspageData.project, subscription.subscriptionAccount.subscriptionTenantId, currentTenantId);
                return token as string;
            }
        }
        return undefined;
    }

    public static getAzureRMServiceEndpointDetails(pageContext: IVssPageContext, subscription?: Subscription, token?: string): ServiceEndpointDetails {
        // Creating service endpoint details for azure spn auth scheme, uses existing VSTS token to make data source calls
        // TODO: Add logic for authenticating with different tenants
        if (!subscription) {
            return {} as ServiceEndpointDetails;
        }
        let parameters: any = {
            accessTokenType: "AppToken",
            tenantId: subscription.subscriptionAccount.subscriptionTenantId
        };

        if (token) {
            parameters["accessToken"] = token;
            parameters["accessTokenFetchingMethod"] = AccessTokenRequestType.Oauth.toString();
        }

        const endpointAuthorization: any = {
            parameters: parameters,
            scheme: EndpointConstants.AuthSchemes.servicePrincipal
        };

        return {
            authorization: endpointAuthorization,
            data: {
                subscriptionId: subscription.id,
                subscriptionName: subscription.displayName,
                environment: EndpointConstants.azureEnvironment,
                scopeLevel: "subscription"
            },
            type: EndpointConstants.EndpointTypes.azureRM,
            url: EndpointConstants.azureManagementUrl
        } as ServiceEndpointDetails;
    }

    public static getEndpointStatus(endpointId: any, context: IVssPageContext): Promise<string> {
        const tfsPageData = getTFSPageData(context);
        if (tfsPageData && tfsPageData.project) {
            const projectName = tfsPageData.project.name;
            return new Promise((resolve, reject) => {
                let isEndpointOperationComplete = false;
                let operationStatus = { "state": "InProgress", "statusMessage": "" };
                function rejectWithEndpointId(message: string) {
                    reject(JSON.stringify({
                        endpointId: endpointId,
                        error: message
                    }));
                }
                let pollTime = setTimeout(function pollEndpointStatus() {
                    if (!isEndpointOperationComplete) {
                        getServiceEndpointClient(context).getServiceEndpointDetails(projectName, endpointId)
                            .then(endpoint => {
                                if (endpoint === null) {
                                    clearTimeout(pollTime);
                                    isEndpointOperationComplete = true;
                                    rejectWithEndpointId(format(Resources.EndpointNotFound, endpointId));
                                }
                                if (!!endpoint.operationStatus) {
                                    operationStatus = endpoint.operationStatus;
                                }
                                if (endpoint.isReady) {
                                    clearTimeout(pollTime);
                                    isEndpointOperationComplete = true;
                                    resolve(operationStatus.state);
                                }
                                else if (operationStatus.state === "Failed") {
                                    clearTimeout(pollTime);
                                    isEndpointOperationComplete = true;
                                    rejectWithEndpointId(operationStatus.statusMessage);
                                }
                                else if (operationStatus.state === "InProgress") {
                                    // recursively scheduling for next poll only once current poll is completed
                                    pollTime = setTimeout(pollEndpointStatus, 2 * 1000);
                                }
                            }, error => {
                                clearTimeout(pollTime);
                                isEndpointOperationComplete = true;
                                rejectWithEndpointId(error);
                            });
                    }
                }, 2 * 1000 /* 2 seconds */);
            });
        }
        // Project information should be present in page context.
        return Promise.reject(Resources.ProjectInfoNotFound);
    }

    public static generateAuxillaryResourcesForEndpoints(templateParameters: any[], endpoints: { [key: string]: object }, contextInfo: { [key: string]: string }): Promise<{ [key: string]: object }> {
        let result: { [key: string]: object } = {};

        const kubeEndpoint: any = endpoints["k8sConnection"];
        const k8sResourceParams = templateParameters.filter(param => param.type.toLocaleLowerCase() === "environmentresource:kubernetes");

        if (k8sResourceParams.length > 0) {
            if (!kubeEndpoint) {
                return Promise.reject(Resources.ResourcesCannotBeCreated);
            }

            for (let index = 0; index < k8sResourceParams.length; index++) {
                const res: KubernetesResourceCreateParametersExistingEndpoint = {
                    name: kubeEndpoint.Data["namespace"],
                    namespace: kubeEndpoint.Data["namespace"],
                    clusterName: EndpointHelper._getClusterNameFromClusterId(kubeEndpoint.Data["clusterId"]),
                    serviceEndpointId: kubeEndpoint.Id,
                    tags: []
                }
                result[k8sResourceParams[index].name] = {
                    "resourcetocreate": res,
                    "type": k8sResourceParams[index].type
                };
            }

            // implicitly adding an environment instance to contain all the resources created 
            result["environment"] = {
                "resourcetocreate": {
                    "name": contextInfo["repositoryName"],
                    "description": `CI/CD setup from this repo: '${contextInfo["repositoryName"]}'`
                },
                "type": "environment"
            };
        }

        return Promise.resolve(result);
    }

}